// Contact.jsx (v4 — cleaner, matches About v3 vibe)
import React, { useMemo, useState } from "react";
import "./Contact.css";
import { useLocalContext } from "../../context/LocalContext";

const Contact = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = {
    name: webinfo.name || "Your Brand",
    email: webinfo.email || "hello@example.com",
    phone: webinfo.phone || "+91 80000 00000",
    phonecall: webinfo.phonecall || "+918000000000",
    address: webinfo.address || "Remote-first • Serving clients worldwide",
    whatsapp: webinfo.whatsapp || "https://wa.me/918000000000",
    mapEmbed:
      webinfo.mapEmbed ||
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609915975!2d72.74109703785945!3d19.082197839145994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b63a9d90e14b%3A0x838fe179b1bdc674!2sMumbai!5e0!3m2!1sen!2sin!4v1719999999999",
    hours: webinfo.hours || "Mon–Sat · 10:00–19:00 IST",
    sla: webinfo.sla || "Avg. first response: 2–4 hours",
  };

  const topics = useMemo(
    () => [
      "Website / landing pages",
      "Redesign / UI refresh",
      "SEO & performance fixes",
      "Paid ads + tracking setup",
      "Automation / dashboard build",
      "Ongoing partnership",
      "Other",
    ],
    []
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    topic: "",
    budget: "",
    message: "",
    consent: false,
    honey: "",
  });

  const [status, setStatus] = useState({ type: "idle", msg: "" });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (form.honey) return "Spam detected.";
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      return "Please enter a valid email.";
    if (!form.message.trim()) return "Please add a short message.";
    if (!form.consent) return "Please agree to be contacted.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "idle", msg: "" });

    const err = validate();
    if (err) return setStatus({ type: "error", msg: err });

    try {
      setStatus({ type: "loading", msg: "Sending…" });

      // TODO: Hook your backend here
      // await fetch("/api/contact", { method:"POST", headers:{ "Content-Type":"application/json" }, body: JSON.stringify(form) });

      await new Promise((r) => setTimeout(r, 800));

      setStatus({
        type: "success",
        msg:
          "Message received. We’ll reply with next steps (and a couple of options) within working hours.",
      });

      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        topic: "",
        budget: "",
        message: "",
        consent: false,
        honey: "",
      });
    } catch {
      setStatus({
        type: "error",
        msg:
          "Couldn’t send right now. Please try again, or use WhatsApp for a faster response.",
      });
    }
  };

  return (
    <section className="contactv4" aria-label="Contact">
      <div className="ct4-wrap">
        {/* Header */}
        <header className="ct4-head">
          <p className="ct4-kicker">Contact</p>
          <h1 className="ct4-title">Let’s talk. Tell us what you need and we’ll suggest the best next step.</h1>
          <p className="ct4-sub">
            Share your goals, timelines, and what “success” looks like. We’ll respond with a clear
            plan: scope, priorities, and the fastest path to launch.
          </p>

          <div className="ct4-badges" aria-hidden="true">
            <span className="chip">{brand.hours}</span>
            <span className="chip">{brand.sla}</span>
          </div>
        </header>

        <div className="ct4-grid">
          {/* LEFT: form */}
          <div className="ct4-formCard">
            <div className="ct4-formHead">
              <h2>Send a quick brief</h2>
              <p>Keep it short — we’ll ask follow-ups only if needed.</p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* honeypot */}
              <input
                type="text"
                name="honey"
                value={form.honey}
                onChange={onChange}
                className="hp"
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
              />

              <div className="row">
                <div className="field">
                  <label htmlFor="name">Full name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Akash Kumar"
                    value={form.name}
                    onChange={onChange}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="akash@company.com"
                    value={form.email}
                    onChange={onChange}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 9XXXX XXXXX"
                    value={form.phone}
                    onChange={onChange}
                  />
                </div>
                <div className="field">
                  <label htmlFor="company">Company (optional)</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    placeholder="Brand / Startup"
                    value={form.company}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label htmlFor="topic">Topic</label>
                  <select
                    id="topic"
                    name="topic"
                    value={form.topic}
                    onChange={onChange}
                  >
                    <option value="">Select…</option>
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="budget">Budget range (optional)</label>
                  <select
                    id="budget"
                    name="budget"
                    value={form.budget}
                    onChange={onChange}
                  >
                    <option value="">Select…</option>
                    <option value="₹25k–₹50k">₹25k–₹50k</option>
                    <option value="₹50k–₹1L">₹50k–₹1L</option>
                    <option value="₹1L–₹2.5L">₹1L–₹2.5L</option>
                    <option value="₹2.5L+">₹2.5L+</option>
                    <option value="$1k–$3k">$1k–$3k</option>
                    <option value="$3k–$8k">$3k–$8k</option>
                    <option value="$8k+">$8k+</option>
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="message">Project details</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  placeholder="What are you building, by when, and what outcome do you want? (Leads, sales, speed, SEO, redesign, automation etc.)"
                  value={form.message}
                  onChange={onChange}
                  required
                />
              </div>

              <label className="check">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={onChange}
                />
                I agree to be contacted about this inquiry.
              </label>

              <div className="actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={status.type === "loading"}
                >
                  {status.type === "loading" ? "Sending…" : "Send message"}
                </button>

                <a
                  className="btn btn-ghost"
                  href={brand.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp instead
                </a>
              </div>

              <div
                className={`form-status ${
                  status.type === "success" ? "ok" : status.type === "error" ? "err" : ""
                }`}
                role="status"
                aria-live="polite"
              >
                {status.msg}
              </div>
            </form>

            <div className="ct4-footnotes">
              <div className="note">
                <strong>What happens next?</strong>
                <p>We reply with a short plan + options (scope, timeline, estimate).</p>
              </div>
              <div className="note">
                <strong>No lock-in</strong>
                <p>This doesn’t start a project — it just helps us understand fit.</p>
              </div>
            </div>
          </div>

          {/* RIGHT: contact cards */}
          <aside className="ct4-side">
            <div className="ct4-sideCard">
              <h2>Reach us directly</h2>
              <p>
                Prefer quick chat? Contact during <strong>{brand.hours}</strong>.
              </p>

              <ul className="ct4-list">
                <li>
                  <span className="k">Call</span>
                  <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
                </li>
                <li>
                  <span className="k">Email</span>
                  <a href={`mailto:${brand.email}`}>{brand.email}</a>
                </li>
                <li>
                  <span className="k">Location</span>
                  <span>{brand.address}</span>
                </li>
              </ul>

              <div className="ct4-quick">
                <a
                  className="btn btn-primary"
                  href={brand.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                >
                  Chat on WhatsApp
                </a>
                <a className="btn btn-ghost" href={`mailto:${brand.email}`}>
                  Send an email
                </a>
              </div>

              <div className="ct4-pills">
                <div className="pill">
                  <span className="pl">Typical work</span>
                  <span className="pv">Websites, SEO, funnels, dashboards</span>
                </div>
                <div className="pill">
                  <span className="pl">Delivery style</span>
                  <span className="pv">Clear scope, weekly updates</span>
                </div>
              </div>
            </div>

            <div className="ct4-sideCard alt">
              <h3>Optional but helpful</h3>
              <ul className="ct4-bullets">
                <li>Link to your website / reference examples</li>
                <li>Deadline or target launch date</li>
                <li>Any tools/stack constraints</li>
              </ul>

              <div className="ct4-map">
                <div className="mapWrap">
                  <iframe
                    title="Map"
                    src={brand.mapEmbed}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <small>Remote-first, we work via calls + async updates.</small>
              </div>
            </div>
          </aside>
        </div>

        {/* small strip */}
        <div className="ct4-strip" aria-hidden="true">
          <span className="s">Fast replies • Clear scope • Clean UI • Performance-first</span>
        </div>
      </div>
    </section>
  );
};

export default Contact;
